import { API, Auth } from "aws-amplify"
import { useQuery, useMutation } from "@tanstack/react-query"
import { CORE_URL } from "utils/functions"
import { downloadBlob } from "components/Invoices/Dashboard/fetchInvoicePdf"

const fetchContacts = async organizationId => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.get(
      "CORE",
      `/contacts/${organizationId}`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchContacts:", error)
    throw error
  }
}

const createContact = async ({ organizationId, contactData, companyId }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        contact: contactData,
        companyId: companyId,
      },
    }
    const response = await API.post(
      "CORE",
      `/contacts/${organizationId}`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in createContact:", error)
    throw error
  }
}

const fetchCompanies = async organizationId => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.get(
      "CORE",
      `/contacts/${organizationId}/companies/light`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchCompanies:", error)
    throw error
  }
}

export const useContacts = (organizationId, enabled = true) => {
  return useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetchContacts(organizationId),
    enabled,
  })
}

export const useCreateContact = () => {
  return useMutation({
    mutationFn: createContact,
  })
}

export const useCompanies = (organizationId, enabled = true) => {
  return useQuery({
    queryKey: ["companies"],
    queryFn: () => fetchCompanies(organizationId),
    enabled,
  })
}

const fetchContactTags = async organization_id => {
  try {
    const params = [
      `organization_id=${organization_id}`,
      "category=F",
      "object_type=contact",
    ].join("&")
    const res = await API.get("CORE", `/tags?${params}`)

    return res
  } catch (error) {
    console.error("❌ Error in fetchContactTags:", error)
    throw error
  }
}

export const useContactTags = (organization_id, enabled = true) => {
  return useQuery({
    queryKey: ["contactTags", organization_id],
    queryFn: () => fetchContactTags(organization_id),
    enabled,
  })
}

const fetchCompanyTags = async organization_id => {
  try {
    const params = [
      `organization_id=${organization_id}`,
      "category=T",
      "object_type=company",
    ].join("&")
    const res = await API.get("CORE", `/tags?${params}`)

    return res
  } catch (error) {
    console.error("❌ Error in fetchCompanyTags:", error)
    throw error
  }
}

export const useCompanyTags = (organization_id, enabled = true) => {
  return useQuery({
    queryKey: ["companyTags", organization_id],
    queryFn: () => fetchCompanyTags(organization_id),
    enabled,
  })
}

const fetchDeleteContact = async ({ organizationId, contactId }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.del(
      "CORE",
      `/contacts/${organizationId}/${encodeURIComponent(contactId)}`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchDeleteContact:", error)
    throw error
  }
}

export const useDeleteContact = () => {
  return useMutation({
    mutationFn: fetchDeleteContact,
  })
}

const fetchEditContact = async ({ contactData, companyId }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        contact: contactData,
        companyId: companyId,
      },
    }
    const response = await API.put("CORE", `/contacts/`, payload)

    return response
  } catch (error) {
    console.error("❌ Error in fetchEditContact:", error)
    throw error
  }
}

export const useEditContact = () => {
  return useMutation({
    mutationFn: fetchEditContact,
  })
}

const fetchCreateContactTag = async ({
  organizationId,
  tagName,
  tagColor,
  category = "F",
  objectType = "contact",
}) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        name: tagName,
        color: tagColor,
        category: category,
        object_type: objectType,
        organization_id: organizationId,
      },
    }
    const response = await API.post("CORE", `/tags/`, payload)

    return response
  } catch (error) {
    console.error("❌ Error in fetchCreateContactTag:", error)
    throw error
  }
}

export const useCreateContactTag = () => {
  return useMutation({
    mutationFn: fetchCreateContactTag,
  })
}

const fetchDeleteContactTag = async ({ organizationId, tagSKs }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        organization_id: organizationId,
        sks: tagSKs,
      },
    }
    const response = await API.del("CORE", `/tags/`, payload)

    return response
  } catch (error) {
    console.error("❌ Error in fetchDeleteContactTag:", error)
    throw error
  }
}

export const useDeleteContactTag = () => {
  return useMutation({
    mutationFn: fetchDeleteContactTag,
  })
}

const fetchCreateCompany = async ({ organizationId, companyData }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: companyData,
    }
    const response = await API.post(
      "CORE",
      `/contacts/${organizationId}/company/`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchCreateCompany:", error)
    throw error
  }
}

export const useCreateCompany = () => {
  return useMutation({
    mutationFn: fetchCreateCompany,
  })
}

const fetchEditCompany = async ({ organizationId, companyData }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()

    companyData.company.PK = organizationId + "#C"
    companyData.company.SK = companyData.SK
    delete companyData.SK

    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        ...companyData,
      },
    }
    console.log(
      "🚀 ~ fetchEditCompany ~ payload.body:",
      JSON.stringify(payload.body, null, 2),
    )

    const response = await API.put("CORE", `/contacts/company/`, payload)
    return response
  } catch (error) {
    console.error("❌ Error in fetchEditCompany:", error)
    throw error
  }
}

export const useEditCompany = () => {
  return useMutation({
    mutationFn: fetchEditCompany,
  })
}

const fetchCreateCompanyTag = async ({
  organizationId,
  tagName,
  tagColor,
  category = "T",
  objectType = "company",
}) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        name: tagName,
        color: tagColor,
        category: category,
        object_type: objectType,
        organization_id: organizationId,
      },
    }
    const response = await API.post("CORE", `/tags/`, payload)

    return response
  } catch (error) {
    console.error("❌ Error in fetchCreateCompanyTag:", error)
    throw error
  }
}

export const useCreateCompanyTag = () => {
  return useMutation({
    mutationFn: fetchCreateCompanyTag,
  })
}

const fetchDeleteCompanyTag = async ({ organizationId, tagSKs }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: {
        organization_id: organizationId,
        sks: tagSKs,
      },
    }
    const response = await API.del("CORE", `/tags/`, payload)

    return response
  } catch (error) {
    console.error("❌ Error in fetchDeleteCompanyTag:", error)
    throw error
  }
}

export const useDeleteCompanyTag = () => {
  return useMutation({
    mutationFn: fetchDeleteCompanyTag,
  })
}

const fetchDeleteCompany = async ({ organizationId, companyId }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.del(
      "CORE",
      `/contacts/${organizationId}/company/${encodeURIComponent(companyId)}`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchDeleteCompany:", error)
    throw error
  }
}

export const useDeleteCompany = () => {
  return useMutation({
    mutationFn: fetchDeleteCompany,
  })
}

const fetchContact = async (organizationId, contactId) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.get(
      "CORE",
      `/contacts/${organizationId}/${encodeURIComponent(contactId)}`,
      payload,
    )

    return response
  } catch (error) {
    console.error("❌ Error in fetchContact:", error)
    throw error
  }
}

export const useContact = (organizationId, contactId, enabled = true) => {
  return useQuery({
    queryKey: ["contact", organizationId, contactId],
    queryFn: () => fetchContact(organizationId, contactId),
    enabled,
  })
}

const fetchCompany = async (organizationId, companyId) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.get(
      "CORE",
      `/contacts/${organizationId}/company/single/${companyId}`,
      payload,
    )
    console.log("🚀 ~ fetchCompany ~ response:", response)
    return response
  } catch (error) {
    console.error("❌ Error in fetchCompany:", error)
    throw error
  }
}

export const useCompany = (organizationId, companyId, enabled = true) => {
  return useQuery({
    queryKey: ["company", organizationId, companyId],
    queryFn: () => fetchCompany(organizationId, companyId),
    enabled,
  })
}

const fetchContactsLight = async organizationId => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    }
    const response = await API.get(
      "CORE",
      `/contacts/${organizationId}/light`,
      payload,
    )
    return response
  } catch (error) {
    console.error("❌ Error in fetchContactsLight:", error)
    throw error
  }
}

const contactDisplayName = contact =>
  `${contact.firstname || ""} ${contact.lastname || ""}`.trim()

const dedupeContactsBySk = contacts => {
  const seen = new Set()
  return contacts.filter(c => {
    const sk = c?.SK
    if (sk == null || sk === "") return true
    if (seen.has(sk)) return false
    seen.add(sk)
    return true
  })
}

export const useContactsLight = (organizationId, enabled = true) => {
  return useQuery({
    queryKey: ["contactsLight", organizationId],
    queryFn: () => fetchContactsLight(organizationId),
    enabled,
    select: data => {
      if (!Array.isArray(data)) return data ?? []
      const unique = dedupeContactsBySk(data)
      return unique.sort((a, b) =>
        contactDisplayName(a).localeCompare(contactDisplayName(b), undefined, {
          sensitivity: "base",
        }),
      )
    },
  })
}

const fetchImportTemplate = async () => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const response = await fetch(`${CORE_URL}/contacts/import/template`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
    })
    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}`)
    }
    const arrayBuffer = await response.arrayBuffer()
    const blob = new Blob([arrayBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    })
    downloadBlob(blob, "contacts_import_template.xlsx")
  } catch (error) {
    console.error("❌ Error in fetchImportTemplate:", error)
    throw error
  }
}

export const useDownloadImportTemplate = () => {
  return useMutation({
    mutationFn: fetchImportTemplate,
  })
}

const fetchImportContacts = async ({ organizationId, file }) => {
  try {
    const token = (await Auth.currentSession()).getIdToken().getJwtToken()
    const formData = new FormData()
    formData.append("file", file)
    const payload = {
      headers: {
        Authorization: `Bearer ${token}`,
        "Access-Control-Allow-Origin": "*",
      },
      body: formData,
    }
    const response = await API.post(
      "CORE",
      `/contacts/import/${organizationId}/upload`,
      payload,
    )
    return response
  } catch (error) {
    console.error("❌ Error in fetchImportContacts:", error)
    throw error
  }
}

export const useImportContacts = () => {
  return useMutation({
    mutationFn: fetchImportContacts,
  })
}
